import React, { useEffect, useLayoutEffect, useState, useCallback } from 'react';
import ScratchCard from 'react-scratchcard';
import { db } from './services/firebase';
import { doc, getDoc, updateDoc, collection, addDoc, getDocs, query, where } from 'firebase/firestore';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Stack,
  Card,
  CardMedia,
  CardContent,
  Alert,
  Paper,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import CelebrationIcon from '@mui/icons-material/Celebration';
import { useNavigate } from 'react-router-dom';
import Confetti from 'react-confetti';
import './styles/ScratchGamePage.css';

enum GameState {
  LOADING = 'loading',
  INITIAL = 'initial',
  SCRATCHING = 'scratching',
  SHOWING_RESULT = 'showing_result', // Nuovo stato per mostrare il risultato
  WON = 'won',
  WAITING = 'waiting',
}

interface Prize {
  id: string;
  nome: string;
  img: string;
  descrizione?: string;
}

interface Coupon {
  id: string;
  userId: string;
  prizeId: string;
  prizeName: string;
  code: string;
  date: string;
  ritirato: boolean;
}

// Aggiungi queste funzioni di utilità prima del componente principale

/**
 * Converte i secondi totali in un oggetto con ore, minuti e secondi
 */
const formatTimeUnits = (totalSeconds: number) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  
  return {
    hours,
    minutes,
    seconds
  };
};

/**
 * Aggiunge uno zero iniziale se necessario (padStart per numeri)
 */
const padZero = (num: number): string => {
  return num.toString().padStart(2, '0');
};

// Aggiorna la configurazione dell'immagine scratch.png
const settings = {
  width: 320, 
  height: 200,
  image: '/scratch.png', // Verifica che questo percorso sia corretto
  finishPercent: 60,
  brushSize: 30,
  customBrush: null, // Usa null per il pennello predefinito
};

function generateCouponCode(): string {
  return 'SCRATCH-' + Date.now() + '-' + Math.random().toString(36).substring(2, 8).toUpperCase();
}

// Aggiungere una funzione di inizializzazione del canvas
const initCanvas = (canvasNode: HTMLCanvasElement | null) => {
  if (!canvasNode) return;
  const ctx = canvasNode.getContext('2d', { willReadFrequently: true });
  return ctx;
};

export default function ScratchGamePage({ user }: { user: any }) {
  const [gameState, setGameState] = useState<GameState>(GameState.LOADING);
  const [prizes, setPrizes] = useState<Prize[]>([]);
  const [selectedPrize, setSelectedPrize] = useState<Prize | null>(null);
  const [coupon, setCoupon] = useState<Coupon | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Nella sezione degli hooks, aggiungi questa funzione
  const canvasRef = useCallback((node: HTMLDivElement | null): void => {
    if (node) {
      const canvas = node.querySelector('canvas');
      if (canvas instanceof HTMLCanvasElement) {
        initCanvas(canvas);
      }
    }
  }, []);

  // Aggiungi questa funzione nel componente
  const checkImageDimensions = (src: string) => {
    const img = new Image();
    img.onload = () => {
      console.log(`Immagine ${src} dimensioni: ${img.width}x${img.height}`);
    };
    img.onerror = () => {
      console.error(`Errore nel caricamento dell'immagine ${src}`);
    };
    img.src = src;
  };

  // Aggiungi questo codice all'inizio del file ScratchGamePage.tsx, dopo gli import
  useEffect(() => {
    // Disattiva temporaneamente gli avvisi di preventDefault in eventi passivi
    const originalConsoleError = console.error;
    console.error = (...args) => {
      if (args[0] && typeof args[0] === 'string' && args[0].includes('preventDefault inside passive')) {
        return;
      }
      return originalConsoleError(...args);
    };

    return () => {
      // Ripristina la funzione console.error originale quando il componente viene smontato
      console.error = originalConsoleError;
    };
  }, []);

  // Within your ScratchGamePage component
  const checkUserStatus = useCallback(async (userId: string) => {
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        return {
          isEligible: userData.isEligible ?? true,
          lastPlayed: userData.lastPlayed,
          attempts: userData.attempts ?? 0
        };
      }
      return { isEligible: true, attempts: 0 };
    } catch (error) {
      console.error('Error checking user status:', error);
      return { isEligible: false, attempts: 0 };
    }
  }, []);

  // Carica i premi all'inizio
  useEffect(() => {
    const fetchPrizes = async () => {
      try {
        const snap = await getDocs(collection(db, 'scratch_prizes'));
        const loadedPrizes = snap.docs.map(d => ({ id: d.id, ...d.data() } as Prize));
        setPrizes(loadedPrizes);
        setGameState(GameState.INITIAL);
      } catch (err) {
        setError('Errore nel caricamento dei premi. Riprova più tardi.');
        console.error('Errore nel caricamento dei premi:', err);
      }
    };

    fetchPrizes();
  }, []);

  // All'inizio del componente, aggiungi questo useEffect per precaricare le immagini
  useEffect(() => {
    // Precaricare l'immagine di scratch
    checkImageDimensions('/scratch.png');
    checkImageDimensions('/niente.png');
    
    // Precaricare le immagini dei premi
    prizes.forEach(prize => {
      const img = new Image();
      img.src = prize.img;
    });
  }, [prizes]);

  // All'inizio del componente, aggiungi questo useLayoutEffect
  useLayoutEffect(() => {
    if (gameState === GameState.SCRATCHING) {
      // Forza il ridimensionamento corretto dell'immagine scratch
      const scratchElements = document.querySelectorAll('.scratchcard-container > div');
      scratchElements.forEach(el => {
        if (el instanceof HTMLElement) {
          el.style.backgroundSize = 'cover';
          el.style.backgroundPosition = 'center';
          el.style.backgroundRepeat = 'no-repeat';
        }
      });
    }
  }, [gameState]);

  // Controlla lo stato iniziale dell'utente
  useEffect(() => {
    const checkInitialState = async () => {
      try {
        // Verifica se l'utente ha premi non riscossi
        const q = query(
          collection(db, 'scratch_coupons'),
          where('userId', '==', user.uid),
          where('ritirato', '==', false)
        );
        const snap = await getDocs(q);

        if (!snap.empty) {
          const win = snap.docs[0].data() as Coupon;
          setCoupon({ ...win, id: snap.docs[0].id });

          // Cerca il premio corrispondente
          const prize = prizes.find(p => p.id === win.prizeId);
          if (prize) {
            setSelectedPrize(prize);
            setGameState(GameState.WON);
          }
          return;
        }
        
        // Se non ha premi non riscossi, verifica se ha già giocato oggi
        const userRef = doc(db, 'utenti', user.uid);
        const userSnap = await getDoc(userRef);
        const data = userSnap.data();
        const today = new Date().toISOString().slice(0, 10);

        if (data?.lastScratch === today) {
          startCountdown();
        } else {
          setGameState(GameState.INITIAL);
        }
      } catch (err) {
        setError('Errore nel controllo dello stato del gioco');
        console.error('Errore nel controllo dello stato del gioco:', err);
      }
    };

    if (prizes.length > 0) {
      checkInitialState();
    }
  }, [prizes, user.uid]);

  // Gestisce il conto alla rovescia
  const startCountdown = useCallback(() => {
    const calculateRemainingSeconds = () => {
      const now = new Date();
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      return Math.floor((tomorrow.getTime() - now.getTime()) / 1000);
    };

    setSecondsLeft(calculateRemainingSeconds());
    setGameState(GameState.WAITING);

    const interval = setInterval(() => {
      const remaining = calculateRemainingSeconds();
      setSecondsLeft(remaining);

      // Effetto visivo quando cambia il secondo
      const secondsBox = document.querySelector('.seconds-box');
      if (secondsBox) {
        secondsBox.classList.add('pulse');
        setTimeout(() => {
          secondsBox.classList.remove('pulse');
        }, 500);
      }

      if (remaining <= 0) {
        clearInterval(interval);
        checkUserStatus(user.uid);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [checkUserStatus, user.uid]);

  // Inizia il gioco
  const startGame = () => {
    if (gameState !== GameState.INITIAL || coupon) {
      console.log("Non è possibile giocare: premio pendente o stato non valido");
      return;
    }

    const isWinner = Math.random() < 0.1 && prizes.length > 0;
    const prize = isWinner ? prizes[Math.floor(Math.random() * prizes.length)] : null;

    setSelectedPrize(prize);
    setGameState(GameState.SCRATCHING);
  };

  // Gestisce il completamento del gratta e vinci
  const handleScratchComplete = async () => {
    // Prima mostra il risultato per qualche secondo
    setGameState(GameState.SHOWING_RESULT);

    if (selectedPrize) {
      const code = generateCouponCode();

      try {
        const docRef = await addDoc(collection(db, 'scratch_coupons'), {
          userId: user.uid,
          prizeId: selectedPrize.id,
          prizeName: selectedPrize.nome,
          code,
          date: new Date().toISOString(),
          ritirato: false,
        });

        const userRef = doc(db, 'utenti', user.uid);
        await updateDoc(userRef, {
          lastScratch: new Date().toISOString().slice(0, 10),
        });

        setCoupon({
          id: docRef.id,
          userId: user.uid,
          prizeId: selectedPrize.id,
          prizeName: selectedPrize.nome,
          code,
          date: new Date().toISOString(),
          ritirato: false,
        });

        setGameState(GameState.WON);
      } catch (err) {
        setError('Errore nel salvataggio della vincita. Riprova più tardi.');
        console.error('Errore nel salvataggio della vincita:', err);
        setGameState(GameState.INITIAL);
      }
    } else {
      try {
        const userRef = doc(db, 'utenti', user.uid);
        await updateDoc(userRef, {
          lastScratch: new Date().toISOString().slice(0, 10),
        });
        
        // Ritarda l'avvio del countdown per vedere l'immagine "niente.png"
        setTimeout(() => {
          startCountdown();
        }, 2000);
      } catch (err) {
        console.error('Errore nell\'aggiornamento dell\'utente:', err);
      }
    }
  };

  // Gestisce la riscossione del premio
  const handleCollectPrize = async () => {
    if (!coupon) return;

    try {
      await updateDoc(doc(db, 'scratch_coupons', coupon.id), {
        ritirato: true,
      });
      setCoupon(null);
      setSelectedPrize(null);
      setGameState(GameState.INITIAL);
    } catch (err) {
      setError('Errore nella riscossione del premio. Riprova più tardi.');
      console.error('Errore nella riscossione del premio:', err);
    }
  };

  if (gameState === GameState.LOADING) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Modifica il componente principale per migliorare la visualizzazione
  return (
    <Box
      sx={{
        maxWidth: { xs: '95%', sm: 600 },
        mx: 'auto',
        p: 3,
        textAlign: 'center',
        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
        borderRadius: 4,
        boxShadow: 3,
        color: 'white',
        position: 'relative',
        overflow: 'hidden', // Per evitare che gli elementi escano dal container
      }}
    >
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
        🎉 Gratta e Vinci 🎉
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {gameState === GameState.INITIAL && (
        <Stack spacing={3} alignItems="center">
          <Box 
            sx={{ 
              position: 'relative',
              width: '100%', 
              maxWidth: 320,
              height: 200,
              mb: 2,
              mx: 'auto',
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
            }}
          >
            <img 
              src="/scratch-card-background.png" 
              alt="Gratta e Vinci" 
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '8px'
              }} 
            />
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(0,0,0,0.3)',
              borderRadius: '8px'
            }}>
              <Typography variant="h5" sx={{ textShadow: '1px 1px 2px #000' }}>
                Pronto a vincere?
              </Typography>
            </Box>
          </Box>
          <Button 
            variant="contained" 
            color="secondary" 
            size="large" 
            sx={{ 
              fontWeight: 'bold',
              py: 1.5,
              px: 4,
              boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.3)',
            }}
            onClick={startGame}
          >
            GRATTA ORA
          </Button>
        </Stack>
      )}

      {gameState === GameState.SCRATCHING && (
        <div className="fixed-height-container">
          <div className="scratchcard-container" ref={canvasRef}>
            <ScratchCard
              {...settings}
              style={{
                width: '320px',
                height: '200px',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                borderRadius: '8px',
                position: 'relative',
                zIndex: 10,
                touchAction: 'none'
              }}
              onComplete={() => {
                setTimeout(() => {
                  handleScratchComplete();
                }, 2000);
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  background: '#fff',
                  borderRadius: '8px'
                }}
              >
                {selectedPrize ? (
                  <img
                    className="scratchcard-content"
                    src={selectedPrize.img}
                    alt={selectedPrize.nome}
                  />
                ) : (
                  <img
                    className="scratchcard-content"
                    src="/niente.png"
                    alt="Nessuna vincita"
                  />
                )}
              </Box>
            </ScratchCard>
          </div>
        </div>
      )}

      {gameState === GameState.SHOWING_RESULT && !selectedPrize && (
        <Box sx={{ width: 320, height: 200, mx: 'auto', mb: 2 }}>
          <img
            src="/niente.png"
            alt="Nessuna vincita"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              borderRadius: '8px',
            }}
          />
        </Box>
      )}

      {gameState === GameState.SHOWING_RESULT && selectedPrize && (
        <Box sx={{ width: 320, height: 200, mx: 'auto', mb: 2 }}>
          <img
            src={selectedPrize.img}
            alt={selectedPrize.nome}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              borderRadius: '8px',
            }}
          />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Hai vinto: {selectedPrize.nome}!
          </Typography>
        </Box>
      )}

      {gameState === GameState.WON && coupon && selectedPrize && (
        <Box>
          <Confetti width={500} height={600} recycle={false} />
          <Card sx={{ mt: 3, background: '#fff', borderRadius: 4 }}>
            <CardMedia component="img" height="200" image={selectedPrize.img} alt={selectedPrize.nome} />
            <CardContent>
              <Typography variant="h5" color="primary">
                🎉 COMPLIMENTI! 🎉
              </Typography>
              <Typography variant="h6" sx={{ my: 1 }}>Hai vinto: {selectedPrize.nome}</Typography>
              <Box sx={{ 
                p: 2, 
                backgroundColor: '#f5f5f5', 
                borderRadius: 1,
                border: '1px dashed #999',
                mb: 2
              }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Codice premio: </Typography>
                <Typography variant="h6" color="secondary" sx={{ fontFamily: 'monospace' }}>
                  {coupon.code}
                </Typography>
              </Box>
              <Alert severity="info" sx={{ mt: 1 }}>
                Mostra questo codice al personale per ritirare il tuo premio!
              </Alert>
            </CardContent>
          </Card>
        </Box>
      )}

      {gameState === GameState.WAITING && (
        <Box sx={{ mt: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Hai già giocato oggi! Torna domani per un'altra possibilità.
          </Typography>
          
          <Paper 
            elevation={3}
            sx={{ 
              p: 2, 
              borderRadius: 2, 
              background: 'linear-gradient(135deg, #2A3B4C 0%, #1E293B 100%)',
              maxWidth: '400px',
              mx: 'auto'
            }}
          >
            <Typography variant="subtitle1" sx={{ mb: 2, color: '#FFC107' }}>
              Tempo Rimanente:
            </Typography>
            
            {/* Orologio digitale */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center',
              backgroundColor: 'rgba(0,0,0,0.3)',
              borderRadius: '12px',
              p: 2,
              boxShadow: 'inset 0 0 10px rgba(0,0,0,0.5), 0 0 5px rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.1)'
            }}>
              {/* Ore */}
              <Box sx={{ 
                backgroundColor: 'rgba(0,0,0,0.6)',
                borderRadius: '8px',
                p: 1.5,
                px: 2,
                mr: 0.5,
                minWidth: '60px',
                display: 'flex',
                justifyContent: 'center',
                fontFamily: '"Digital-7", "DS-Digital", monospace',
                boxShadow: 'inset 0 0 6px rgba(0,255,255,0.3)'
              }}>
                <Typography variant="h4" sx={{ 
                  fontWeight: 'bold', 
                  color: '#0ff',
                  textShadow: '0 0 5px #0ff', 
                  letterSpacing: '2px',
                  fontFamily: 'inherit'
                }}>
                  {padZero(formatTimeUnits(secondsLeft).hours)}
                </Typography>
              </Box>
              
              {/* Separatore lampeggiante */}
              <Box 
                className="blink-animation"
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  px: 0.2
                }}
              >
                <Typography variant="h4" sx={{ 
                  fontWeight: 'bold',
                  color: '#0ff',
                  textShadow: '0 0 5px #0ff'
                }}>:</Typography>
              </Box>
              
              {/* Minuti */}
              <Box sx={{ 
                backgroundColor: 'rgba(0,0,0,0.6)',
                borderRadius: '8px',
                p: 1.5,
                px: 2,
                mx: 0.5,
                minWidth: '60px',
                display: 'flex',
                justifyContent: 'center',
                fontFamily: '"Digital-7", "DS-Digital", monospace',
                boxShadow: 'inset 0 0 6px rgba(0,255,255,0.3)'
              }}>
                <Typography variant="h4" sx={{ 
                  fontWeight: 'bold', 
                  color: '#0ff',
                  textShadow: '0 0 5px #0ff', 
                  letterSpacing: '2px',
                  fontFamily: 'inherit'
                }}>
                  {padZero(formatTimeUnits(secondsLeft).minutes)}
                </Typography>
              </Box>
              
              {/* Separatore lampeggiante */}
              <Box 
                className="blink-animation"
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  px: 0.2
                }}
              >
                <Typography variant="h4" sx={{ 
                  fontWeight: 'bold',
                  color: '#0ff',
                  textShadow: '0 0 5px #0ff'
                }}>:</Typography>
              </Box>
              
              {/* Secondi */}
              <Box sx={{ 
                backgroundColor: 'rgba(0,0,0,0.6)',
                borderRadius: '8px',
                p: 1.5,
                px: 2,
                ml: 0.5,
                minWidth: '60px',
                display: 'flex',
                justifyContent: 'center',
                fontFamily: '"Digital-7", "DS-Digital", monospace',
                boxShadow: 'inset 0 0 6px rgba(0,255,255,0.3)'
              }}>
                <Typography variant="h4" sx={{ 
                  fontWeight: 'bold', 
                  color: '#0ff',
                  textShadow: '0 0 5px #0ff', 
                  letterSpacing: '2px',
                  fontFamily: 'inherit'
                }}>
                  {padZero(formatTimeUnits(secondsLeft).seconds)}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ color: '#aaa' }}>
                Ritorna domani per una nuova chance di vincita!
              </Typography>
            </Box>
          </Paper>
        </Box>
      )}

      <Button
        startIcon={<HomeIcon />}
        variant="outlined"
        color="inherit"
        sx={{ 
          mt: 3,
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
          }
        }}
        onClick={() => navigate('/')}
      >
        Torna alla Home
      </Button>
    </Box>
  );
}
